import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LeadFormSection } from "@/components/LeadFormSection";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { GoogleReviewBadge } from "@/components/GoogleReviewBadge";
import { TestimonialCards } from "@/components/TestimonialCards";
import { PricingSection } from "@/components/PricingSection";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle,
  Phone,
  Star,
  TrendingUp,
  FileText,
  Image,
  MessageSquare,
  Layout,
  Smartphone,
  MapPin,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const LawnCareWebsiteDesign = () => {
  const faqs = [
    {
      question: "How much does a lawn care website cost?",
      answer:
        "Our Core Website plan is $200/month with everything included—design, hosting, maintenance, and support. For businesses serving multiple locations, our Local SEO Expansion plan is $500/month. No upfront costs, and the build is included in your monthly plan.",
    },
    {
      question: "How long does it take to build my website?",
      answer:
        "Most sites are designed, built, and ready to launch in about two weeks—typically 10–14 days. You'll see a preview quickly, give feedback, and we refine it until you're satisfied.",
    },
    {
      question: "What pages are included?",
      answer:
        "A complete website typically includes 7–14 pages: Home, Services (or individual service pages), Service Areas, About, Reviews/Testimonials, Contact, FAQ, and Privacy/Accessibility pages. We customize based on what your business offers.",
    },
    {
      question: "Do you help with local SEO?",
      answer:
        "Yes. Every website we build is structured for local SEO—service area pages targeting specific cities, proper headings, meta tags, schema markup, mobile-first design, and fast load times. This helps you rank where you actually work.",
    },
    {
      question: "What's included in the monthly subscription?",
      answer:
        "Your subscription covers: custom website design, hosting, SSL security, maintenance, content updates, mobile-responsive design, local SEO structure, quote forms, photo galleries, review sections, and ongoing support.",
    },
    {
      question: "Can I make changes after my site launches?",
      answer:
        "Absolutely. Need to add a service, update photos, or expand your service area? Just send us a message—we handle edits at no extra cost for reasonable requests.",
    },
    {
      question: "Do I own my website?",
      answer:
        "You own your content, photos, and copy. The website is hosted on our platform as part of the monthly service. If you ever cancel, we can provide your content and help transition to another host.",
    },
    {
      question: "What do I need to provide to get started?",
      answer:
        "We need your logo (if you have one), photos of your work (phone photos are fine), a list of services you offer, and your service area. We handle everything else.",
    },
    {
      question: "What if I want to cancel?",
      answer:
        "No contracts. You can cancel anytime, month-to-month. We believe if the website is working for you, you'll want to keep it.",
    },
    {
      question: "Do I need a website if I already have a Facebook page?",
      answer:
        "Yes. A professional website captures high-intent Google searches that Facebook can't. Homeowners searching 'lawn care near me' want to find a real business site, see photos of your work, and contact you directly. Facebook alone leaves leads on the table.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-design/#faq",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    name: "Leaf Rush Marketing",
    url: "https://www.leafrushmarketing.com",
    logo: "https://www.leafrushmarketing.com/og-image.jpg",
    description:
      "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients.",
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-design/#webpage",
    name: "Lawn Care Website Design That Gets Clients | Leaf Rush Marketing",
    url: "https://www.leafrushmarketing.com/lawn-care-website-design/",
    description:
      "Done-for-you lawn care web design built to drive calls and quote requests. $0 upfront, $200–$300/mo. Fast mobile site, service pages, reviews.",
    isPartOf: {
      "@id": "https://www.leafrushmarketing.com/#organization",
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-design/#service",
    name: "Lawn Care Website Design",
    provider: {
      "@id": "https://www.leafrushmarketing.com/#organization",
    },
    description:
      "Professional websites for lawn care companies that convert visitors into calls, quotes, and recurring clients. Includes hosting, maintenance, and ongoing support.",
    areaServed: "United States",
    serviceType: "Website Design",
  };

  return (
    <>
      <SEO
        title="Lawn Care Website Design That Gets Clients"
        description="Done-for-you lawn care web design built to drive calls and quote requests. $0 upfront, $200–$300/mo. Fast mobile site, service pages, reviews."
        keywords="lawn care website design, website for lawn care company, lawn care web design, lawn mowing website, lawn care quote form, lawn care service pages, local SEO website structure"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-design/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-design/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Done-for-You Websites
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Professional Lawn Care Website Design—Done for You
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                We build websites that turn local searches into calls, quotes, and recurring jobs. Complete service: design, SEO structure, hosting, and ongoing support.
              </p>

              {/* Trust items */}
              <div className="flex flex-wrap gap-4 justify-center items-center text-sm font-medium max-w-2xl mx-auto pt-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>$0 upfront</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>$200–$300/mo</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Typically live in 10–14 days</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Month-to-month</span>
                </div>
              </div>

              {/* Google Review Badge */}
              <div className="flex justify-center pt-2">
                <GoogleReviewBadge rating={5.0} />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <a href="#examples">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    See Website Examples
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="py-12 bg-accent/20 border-y">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Who This Is For</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">New businesses</strong> that need to look established and
                    compete with bigger companies from day one
                  </p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Growing operations</strong> that want a site that drives calls,
                    not just one that "looks nice"
                  </p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Owners frustrated with DIY sites</strong> that took forever to
                    build and still don't convert visitors
                  </p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Mowing crews adding landscaping</strong> who need dedicated
                    pages for each service they now offer
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">What's Included</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Everything needed to look professional, rank locally, and convert visitors into paying customers.
              </p>
            </div>

            <h3 className="text-xl font-semibold mb-6 text-center max-w-6xl mx-auto">Pages, Features & SEO Setup</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card>
                <CardHeader className="pb-3">
                  <Layout className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">7–84 Pages Built for Local SEO</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Home, services, service areas, about, reviews, contact, FAQ, privacy—structured to rank in your
                    target cities.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <FileText className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Dedicated Service Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Mowing, edging, fertilization, cleanups, landscaping—each service gets its own page optimized to
                    convert.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <MapPin className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Service Area Targeting</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Pages for the cities and ZIPs you actually serve—so you rank where you want to work, not everywhere.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <MessageSquare className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Short 4-Field Quote Form</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Name, phone, city, service needed. No friction, no 10-field forms. Just leads.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <Phone className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Click-to-Call + Sticky Mobile Button</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    One tap to call on mobile. We recommend a sticky call button so it's always visible while scrolling.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <Image className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Before/After Gallery Setup</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Showcase your work with side-by-side photos that build trust and prove quality at a glance.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <Star className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Reviews & Testimonials Section</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Display Google reviews or written testimonials to build credibility with first-time visitors.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <Smartphone className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Mobile-First + Fast Load</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Most leads come from phones. Your site loads fast (WebP images, lazy loading) and works perfectly on
                    any device.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <TrendingUp className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">On-Page SEO Setup</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Proper titles, headings, internal links, and schema markup—so Google understands your pages and
                    ranks them.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-10 space-y-4">
              <NavLink to="/websites">
                <Button size="lg" variant="outline">
                  See Pricing Details
                </Button>
              </NavLink>
              <div className="flex flex-wrap gap-4 justify-center pt-4">
                <NavLink
                  to="/lawn-care-website-design-pricing/"
                  className="text-primary underline hover:no-underline text-sm"
                >
                  Lawn Care Website Design Pricing →
                </NavLink>
                <NavLink
                  to="/lawn-care-website-redesign/"
                  className="text-primary underline hover:no-underline text-sm"
                >
                  Lawn Care Website Redesign →
                </NavLink>
                <NavLink
                  to="/lawn-care-website-seo-services/"
                  className="text-primary underline hover:no-underline text-sm"
                >
                  Lawn Care Website SEO Services →
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Homepage Layout That Converts */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">Homepage Layout That Converts</h2>
              <p className="text-lg text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Every site we build follows this proven structure to turn visitors into leads.
              </p>

              <h3 className="text-xl font-semibold mb-6">The 6-Part Formula</h3>
              <div className="bg-background rounded-lg border p-6 md:p-8">
                <ol className="space-y-6">
                  <li className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Headline = Service + City</p>
                      <p className="text-muted-foreground">
                        "Lawn Care Services in [Your City]"—visitors instantly know what you do and where.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-semibold text-lg">1 Clear CTA Above the Fold</p>
                      <p className="text-muted-foreground">
                        "Get a Free Quote" or "Call Now"—one obvious action, no confusion.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-semibold text-lg">2–4 Trust Builders</p>
                      <p className="text-muted-foreground">
                        "Licensed & Insured" • "Free Estimates" • "Same-Week Service" • "5-Star Rated"
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                      4
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Before/After Photos</p>
                      <p className="text-muted-foreground">
                        Real images of your work—proof of quality that builds instant credibility.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                      5
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Customer Reviews</p>
                      <p className="text-muted-foreground">
                        Google reviews or testimonials with names—social proof that closes the deal.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold">
                      6
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Short Quote Form (4 fields) + Sticky Call Button</p>
                      <p className="text-muted-foreground">
                        Name, phone, city, service. Plus a sticky call button on mobile so visitors can tap to call
                        anytime.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Must-Have Pages */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">Must-Have Pages</h2>
              <p className="text-lg text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Every professional lawn mowing website needs these.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { page: "Home", desc: "Headline, CTA, trust builders, and proof above the fold" },
                  { page: "Services (or individual pages)", desc: "Each service explained with benefits and a CTA" },
                  { page: "Service Areas", desc: "Cities and neighborhoods you cover—critical for local ranking" },
                  { page: "About", desc: "Your story, experience, and why customers trust you" },
                  { page: "Reviews / Testimonials", desc: "Social proof that builds confidence" },
                  { page: "Contact", desc: "Short form, phone number, hours" },
                  { page: "FAQ", desc: "Answers common questions, reduces friction" },
                  { page: "Privacy / Accessibility", desc: "Required legal pages" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-accent/30 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">{item.page}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">How It Works</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From first call to live site—typically 10–14 days.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              <div className="text-center space-y-4">
                <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  1
                </div>
                <p className="font-semibold text-lg">15-Min Strategy Call</p>
                <p className="text-sm text-muted-foreground">
                  We learn about your services, goals, and service area. Quick and focused.
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  2
                </div>
                <p className="font-semibold text-lg">We Build Your Site</p>
                <p className="text-sm text-muted-foreground">
                  You send photos and info. We design and develop your site—typically in 3–5 days.
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  3
                </div>
                <p className="font-semibold text-lg">You Review & Approve</p>
                <p className="text-sm text-muted-foreground">
                  See a preview, request changes, and we revise until you're happy.
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                  4
                </div>
                <p className="font-semibold text-lg">Launch & Get Leads</p>
                <p className="text-sm text-muted-foreground">
                  Go live and start getting calls. We handle hosting, updates, and support.
                </p>
              </div>
            </div>

            {/* After the call */}
            <div className="max-w-2xl mx-auto mt-12 bg-background rounded-lg border p-6">
              <p className="font-semibold text-center mb-4">After the Call</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  We send a short questionnaire (takes ~10 min)
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  You send logo, photos, and service list
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  We send a preview link within days
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  You review, we revise, you only pay when you approve
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">Common Mistakes We Fix</h2>
              <p className="text-lg text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Most sites fail for these avoidable reasons.
              </p>

              <h3 className="text-xl font-semibold mb-6 text-center">Problems We Solve</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { mistake: "No clear service area", fix: "Dedicated service area pages for each city you cover" },
                  { mistake: "No photos of work", fix: "Before/after galleries that build trust instantly" },
                  {
                    mistake: "No proof near the top",
                    fix: "Photos and reviews above the fold—not buried at the bottom",
                  },
                  { mistake: "Weak or buried CTA", fix: "Clear 'Get a Quote' and 'Call Now' buttons above the fold" },
                  { mistake: "Long, complicated forms", fix: "Simple 4-field form: Name, Phone, City, Service" },
                  { mistake: "Slow mobile experience", fix: "Mobile-first design that loads fast on any device" },
                  {
                    mistake: "Cookie-cutter, one-size-fits-all layout",
                    fix: "Custom design tailored to your brand, services, and area",
                  },
                ].map((item, i) => (
                  <Card key={i} className="border-destructive/20">
                    <CardContent className="pt-6">
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0 text-lg">
                          ✕
                        </div>
                        <div>
                          <p className="font-semibold text-destructive">{item.mistake}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            <span className="text-primary font-medium">Our fix:</span> {item.fix}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Proof / Testimonials */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <TestimonialCards
              title="What Our Clients Say"
              subtitle="Real Google reviews from business owners we've worked with"
            />
          </div>
        </section>

        {/* Examples of What We Build */}
        <section id="examples" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">Examples of What We Build</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                No portfolio needed. Here's what every site includes.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card>
                <CardHeader className="pb-3">
                  <Layout className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Homepage Hero + CTA Layout</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    A clean hero section with your headline, trust badges, and one clear call-to-action.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Why it converts:</strong> Visitors know what you do and how to reach you in seconds
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>What we need:</strong> Your tagline or service focus, phone number
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <FileText className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Service Page Layout</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Individual pages for each service (mowing, edging, cleanups) optimized to rank and convert.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Why it converts:</strong> Each service ranks separately and answers specific buyer
                        questions
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>What we need:</strong> List of services you offer
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <MapPin className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Service Area Section</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Pages or sections targeting the cities and neighborhoods you actually serve.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Why it converts:</strong> Ranks in local searches and reassures visitors you cover their
                        area
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>What we need:</strong> List of cities, ZIPs, or neighborhoods
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <Star className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Reviews Block Layout</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    A dedicated section for testimonials or embedded Google reviews.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Why it converts:</strong> Social proof builds trust with first-time visitors
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>What we need:</strong> Google review link or written testimonials
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <MessageSquare className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Short Quote Form Layout</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    A simple 4-field form (name, phone, city, service) that captures leads without friction.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Why it converts:</strong> Short forms get more submissions than long ones
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>What we need:</strong> Email to receive leads
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <Image className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Before/After Gallery</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Side-by-side photo layouts that show real results from your work.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Why it converts:</strong> Visual proof of quality beats paragraphs of copy
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>What we need:</strong> Before/after photos (phone photos work)
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <a href="#get-started">
                <Button size="lg" className="w-full sm:w-auto">
                  <Phone className="mr-2 h-5 w-5" />
                  Get Started
                </Button>
              </a>
              <NavLink to="/websites" className="text-primary hover:underline flex items-center justify-center">
                See full website subscription details →
              </NavLink>
            </div>
          </div>
        </section>

        {/* What We Need From You */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">What We Need From You</h2>
              <p className="text-lg text-muted-foreground text-center mb-10">Getting started is simple.</p>

              <div className="bg-background rounded-lg border p-6 md:p-8">
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Your logo</p>
                      <p className="text-sm text-muted-foreground">
                        If you have one. If not, we can work with your business name.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Photos of your work</p>
                      <p className="text-sm text-muted-foreground">
                        Before/after shots, job photos, crew photos. Phone photos work fine.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">List of services</p>
                      <p className="text-sm text-muted-foreground">
                        Mowing, edging, fertilization, cleanups—whatever you offer.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Service area</p>
                      <p className="text-sm text-muted-foreground">
                        Cities, neighborhoods, or zip codes where you work.
                      </p>
                    </div>
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground mt-6 text-center">
                  That's it. We handle design, development, hosting, and updates.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Common questions about our{" "}
                <NavLink to="/websites" className="text-primary hover:underline">
                  website services
                </NavLink>
                .
              </p>
            </div>

            <Accordion type="single" collapsible className="max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Pricing Section */}
        <PricingSection variant="full" />

        {/* Related Guides */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Related Guides</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <NavLink to="/lawn-care-website-examples/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Lawn Care Website Examples</p>
                      <p className="text-sm text-muted-foreground">
                        See section layouts that convert visitors into leads.
                      </p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/lawn-care-website-cost/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Lawn Care Website Cost</p>
                      <p className="text-sm text-muted-foreground">
                        Compare DIY, freelancer, and subscription pricing.
                      </p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/lawn-care-website-content/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Lawn Care Website Content</p>
                      <p className="text-sm text-muted-foreground">What to write on each page of your website.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/lawn-care-website-seo/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Lawn Care Website SEO</p>
                      <p className="text-sm text-muted-foreground">On-page SEO basics for lawn care websites.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/service-pages-that-rank/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Service Pages That Rank</p>
                      <p className="text-sm text-muted-foreground">
                        Structure service pages for rankings and conversions.
                      </p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/service-area-pages-local-seo/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Service Area Pages for Local SEO</p>
                      <p className="text-sm text-muted-foreground">Rank in nearby cities with location pages.</p>
                    </CardContent>
                  </Card>
                </NavLink>
              </div>

              {/* Hub Links */}
              <div className="mt-8 grid md:grid-cols-4 gap-4">
                <NavLink
                  to="/comparisons"
                  className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors text-center"
                >
                  <p className="font-semibold text-sm">Comparisons Hub</p>
                </NavLink>
                <NavLink
                  to="/website-fixes"
                  className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors text-center"
                >
                  <p className="font-semibold text-sm">Website Fixes Hub</p>
                </NavLink>
                <NavLink
                  to="/website-templates"
                  className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors text-center"
                >
                  <p className="font-semibold text-sm">Templates Hub</p>
                </NavLink>
                <NavLink
                  to="/resources"
                  className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors text-center"
                >
                  <p className="font-semibold text-sm">Resources Hub</p>
                </NavLink>
              </div>

              {/* Cross-link to landscaping */}
              <div className="mt-8 p-4 bg-card rounded-lg border text-center">
                <p className="text-muted-foreground">
                  <strong>Also offer landscaping services?</strong> Check out our{" "}
                  <NavLink to="/landscaping-website-design" className="text-primary hover:underline">
                    landscaping website design
                  </NavLink>{" "}
                  page.
                </p>
              </div>

              <div className="text-center mt-6">
                <NavLink to="/websites" className="text-primary hover:underline text-sm">
                  Website subscription pricing →
                </NavLink>
                {" • "}
                <NavLink to="/blog" className="text-primary hover:underline text-sm">
                  Browse all blog posts →
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-2xl md:text-4xl font-bold">Ready to Get a Site That Drives Calls?</h2>
              <p className="text-lg text-muted-foreground">
                Book a quick call. We'll learn about your business, answer your questions, and explain how we can
                help—no pressure.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <div className="container px-4 md:px-6 pb-8">
          <p className="text-sm text-muted-foreground text-center">Last updated: December 20, 2024</p>
        </div>

        {/* Lead Form + Calendly */}
        <LeadFormSection />
      </div>
    </>
  );
};

export default LawnCareWebsiteDesign;
